import { getFlatAllBlock } from '@lighthouse/block'
import type { BlockAbstract, ChartBlockAbstract, ContainerBlockAbstract, DataSourceAbstract, ViewOptions } from '@lighthouse/core'
import { BlockType, ChartType } from '@lighthouse/core'
import { chartTypeLevel, createBlockFinder, findBlockById, getViewColumns, useAtomData } from '@lighthouse/shared'
import { nanoid } from '@lighthouse/tools'
import { current, isDraft, original } from 'immer'
import { useAtomCallback } from 'jotai/utils'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { debounce } from 'throttle-debounce'
import type { MakeADTMember } from 'ts-adt/MakeADT'
import { makeMatchI } from 'ts-adt/MakeADT'

import { currentBreakPointIdAtom, mainBreakPointAtom, syncComponentsAtom } from '@/atoms/application/state'
import { createBlockAtom, initContainerRuntimeStateAtom, setContainerRuntimeStateAtom } from '@/atoms/page/action'
import {
    blockCreatingListAtom,
    blocksAtom,
    containerRuntimeStateAtomFamily,
    lastPageOfStackAtom,
    pageBlocksAtom,
    pageInternalStateAtomFamily
} from '@/atoms/page/state'
import { BlockSettings } from '@/components/BlockSettings'
import { generateCalendarConfig, generateGalleryConfig } from '@/constants/Block/generate/view'
import { getIndicatorOption } from '@/constants/Block/sharedStyle'
import { copyBlock } from '@/hooks/layoutEngine/utils'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import type { BlockUpdateOption } from '@/hooks/useBlock'
import { useBlockActions } from '@/hooks/useBlock'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'
import { useCurrentLanguagePageList } from '@/hooks/usePage'
import * as srv from '@/services'
import { BLOCK_UPDATE_ACTION, NODE_UPDATE_ACTION, pageUndoRedoController } from '@/utils/undoRedo/page/controller'

import {
    cleanBarConfig,
    cleanCalendarConfig,
    cleanGalleryConfig,
    cleanIndicatorConfig,
    cleanKanbanConfig,
    cleanPieConfig,
    cleanRadarConfig,
    cleanStriationConfig
} from './constants'
import type { BlockSettingActionADT } from './listener'
import { BlockSettingMitt } from './listener'

const getInitChartBlockConfigurator = function (block: ChartBlockAbstract, dataSource: DataSourceAbstract): ChartBlockAbstract {
    const { schema, appId, id: dsId } = dataSource
    const textField = Object.values(schema).find(f => f.type === 'text' || (f.type === 'aggregation' && f.innerType === 'TEXT'))
    const textFieldId = textField?.id || 'ID'
    const numberField = Object.values(schema).find(f => f.type === 'number' || (f.type === 'aggregation' && f.innerType === 'NUMBER'))
    const numberFieldId = numberField?.id
    const calcType = numberField ? 'max' : 'count'
    switch (block.config.chartType) {
        case 'bar':
        case 'striation': {
            return {
                ...block,
                config: {
                    ...block.config,
                    pointer: dsId,
                    appId,
                    dimensions: [{ id: nanoid(), fieldId: textField?.id || 'ID' }],
                    mainAxis: numberFieldId
                        ? [
                              {
                                  id: nanoid(),
                                  fieldId: numberFieldId,
                                  calcType,
                                  chartType: 'bar'
                              }
                          ]
                        : [],
                    linkFilterController: {
                        expression: {
                            where: 'AND',
                            conditions: []
                        }
                    }
                }
            }
        }
        case 'line': {
            return {
                ...block,
                config: {
                    ...block.config,
                    pointer: dsId,
                    appId,
                    dimensions: [{ id: nanoid(), fieldId: textField?.id || 'ID' }],
                    mainAxis: numberFieldId
                        ? [
                              {
                                  id: nanoid(),
                                  fieldId: numberFieldId,
                                  calcType,
                                  chartType: 'line',
                                  showArea: false,
                                  showSymbol: true,
                                  lineType: 'smooth'
                              }
                          ]
                        : [],
                    linkFilterController: {
                        expression: {
                            where: 'AND',
                            conditions: []
                        }
                    }
                }
            }
        }
        case 'funnel':
        case 'pie': {
            return {
                ...block,
                config: {
                    ...block.config,
                    pointer: dsId,
                    appId,
                    dimensions: [{ id: nanoid(), fieldId: textField?.id || 'ID' }],
                    mainAxis: numberFieldId
                        ? [
                              {
                                  id: nanoid(),
                                  fieldId: numberFieldId,
                                  calcType
                              }
                          ]
                        : [],
                    linkFilterController: {
                        expression: {
                            where: 'AND',
                            conditions: []
                        }
                    }
                }
            }
        }
        case 'composite': {
            const secondNumberField = Object.values(schema).find(f => f.type === 'number' && f.id !== numberFieldId)
            const secondCalcType = secondNumberField ? 'sum' : calcType
            return {
                ...block,
                config: {
                    ...block.config,
                    pointer: dsId,
                    appId,
                    dimensions: [{ id: nanoid(), fieldId: textField?.id || 'ID' }],
                    mainAxis: numberFieldId
                        ? [
                              {
                                  id: nanoid(),
                                  fieldId: numberFieldId,
                                  calcType,
                                  chartType: 'bar'
                              }
                          ]
                        : [],
                    linkFilterController: {
                        expression: {
                            where: 'AND',
                            conditions: []
                        }
                    },
                    showSecondaryAxis: !!secondNumberField,
                    secondaryAxis: secondNumberField
                        ? [
                              {
                                  id: nanoid(),
                                  fieldId: secondNumberField?.id,
                                  calcType: secondCalcType,
                                  chartType: 'line',
                                  showArea: false,
                                  showSymbol: true,
                                  lineType: 'straight'
                              }
                          ]
                        : []
                }
            }
        }
        case 'indicator': {
            return {
                ...block,
                config: {
                    ...block.config,
                    ...getIndicatorOption(),
                    appId,
                    pointer: dsId,
                    fieldId: numberFieldId,
                    calcType
                }
            }
        }
        default: {
            return block
        }
    }
}

const BlockSettingsController = React.forwardRef<HTMLDivElement>((props, ref) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const { pageId, stackId } = useAtomData(
        lastPageOfStackAtom,
        useCallback(
            s => ({
                pageId: s?.pageId ?? '',
                stackId: s?.stackId ?? ''
            }),
            []
        )
    )
    const selectedNode = useAtomData(
        pageInternalStateAtomFamily(stackId),
        useCallback(s => (s.selectedNodes && s.selectedNodes.length === 1 ? s.selectedNodes[0] : undefined), [])
    )

    const syncComponents = useAtomData(syncComponentsAtom)

    const selectedBlock = useAtomData(
        blocksAtom,
        useCallback(
            s => {
                const blocks = s[pageId]
                if (!blocks) {
                    return
                }

                if (!selectedNode) {
                    return
                }

                // 使用 createBlockFinder 优化查找性能，避免重复树遍历
                const blockFinder = createBlockFinder(blocks, syncComponents)
                return blockFinder.find(selectedNode)
            },
            [pageId, selectedNode, syncComponents]
        )
    )

    const { onUpdateBlock } = useBlockActions(pageId, stackId)

    const pointer = useMemo(() => {
        if (!selectedBlock) {
            return ''
        }
        switch (selectedBlock.type) {
            case 'view': {
                return selectedBlock.config.pointer || ''
            }
            case 'chart': {
                if (selectedBlock.config.chartType === ChartType.indicator) {
                    return ''
                }
                return selectedBlock.config.pointer
            }
            default: {
                return ''
            }
        }
    }, [selectedBlock])

    const dataSourceList = useDataSourceList(appId, envId)

    const dataSource = useDataSource(appId, envId, pointer)

    const allPages = useCurrentLanguagePageList()

    const debounceAdd = useMemo(
        () =>
            debounce(500, ({ prevBlock, nextBlock }: BlockUpdateOption) => {
                pageUndoRedoController.add({
                    action: BLOCK_UPDATE_ACTION,
                    payload: {
                        prev: prevBlock,
                        next: nextBlock
                    }
                })
            }),
        []
    )

    const blockSettingActionRef = useRef<BlockSettingActionADT | null>(null)

    useEffect(() => {
        function handle(payload: BlockSettingActionADT) {
            blockSettingActionRef.current = payload
        }

        BlockSettingMitt.on('event', handle)

        return () => {
            BlockSettingMitt.off('event', handle)
        }
    }, [])

    const [containerConfigureLoading, setContainerConfigureLoading] = useState(false)

    const handleCreateContainerView = useAtomCallback(
        useCallback(
            async (get, set, value: BlockAbstract, payload: MakeADTMember<'action', BlockSettingActionADT, 'createContainerView'>) => {
                if (!selectedBlock) {
                    return
                }

                const isSyncComponent = !!selectedBlock.isMasterSynchronous || !!selectedBlock.isLeafSynchronous

                if (isSyncComponent) {
                    let prevSyncComponents: ContainerBlockAbstract[] | undefined
                    let nextSyncComponents: ContainerBlockAbstract[] | undefined
                    set(syncComponentsAtom, draft => {
                        const draftBlock = findBlockById<ContainerBlockAbstract>(selectedBlock.id, draft)
                        if (!draftBlock) {
                            return
                        }

                        Object.entries(value).forEach(([k, v]) => {
                            Reflect.set(draftBlock, k, v)
                        })

                        draftBlock.children.push({ id: payload.newId, children: [] })

                        prevSyncComponents = original(draft)
                        nextSyncComponents = current(draft)

                        set(initContainerRuntimeStateAtom, {
                            stackId,
                            blocks: get(pageBlocksAtom(pageId)),
                            syncComponents: nextSyncComponents || []
                        })
                    })

                    if (!nextSyncComponents) {
                        return
                    }

                    pageUndoRedoController.add({
                        action: NODE_UPDATE_ACTION,
                        payload: {
                            prev: {
                                blocks: undefined,
                                syncComponents: prevSyncComponents
                            },
                            next: {
                                blocks: undefined,
                                syncComponents: nextSyncComponents
                            }
                        }
                    })

                    setContainerConfigureLoading(true)
                    await srv.updateSyncComponents(nextSyncComponents)
                    setContainerConfigureLoading(false)
                } else {
                    let nextBlock: BlockAbstract | undefined
                    let prevBlocks: BlockAbstract[] | undefined
                    let nextBlocks: BlockAbstract[] | undefined

                    set(blocksAtom, draft => {
                        const draftBlocks = draft[pageId]
                        if (!draftBlocks) {
                            return
                        }

                        const draftBlock = findBlockById<ContainerBlockAbstract>(selectedBlock.id, draftBlocks)
                        if (!draftBlock) {
                            return
                        }

                        Object.entries(value).forEach(([k, v]) => {
                            Reflect.set(draftBlock, k, v)
                        })

                        draftBlock.children.push({ id: payload.newId, children: [] })
                        nextBlock = current(draftBlock)
                        prevBlocks = original(draftBlocks)
                        nextBlocks = current(draftBlocks)
                    })

                    if (!nextBlock || !nextBlocks) {
                        return
                    }

                    pageUndoRedoController.add({
                        action: NODE_UPDATE_ACTION,
                        payload: {
                            prev: {
                                blocks: prevBlocks,
                                syncComponents: undefined
                            },
                            next: {
                                blocks: nextBlocks,
                                syncComponents: undefined
                            }
                        }
                    })

                    setContainerConfigureLoading(true)
                    await srv.updateBlock({ updatedBlocks: [nextBlock], allBlocks: nextBlocks, pageId })
                    setContainerConfigureLoading(false)
                }
            },
            [pageId, selectedBlock, stackId]
        )
    )

    const handleDeleteContainerView = useAtomCallback(
        useCallback(
            async (get, set, value: BlockAbstract, payload: MakeADTMember<'action', BlockSettingActionADT, 'deleteContainerView'>) => {
                if (!selectedBlock) {
                    return
                }

                const isSyncComponent = !!selectedBlock.isMasterSynchronous || !!selectedBlock.isLeafSynchronous
                const mainBreakPoint = get(mainBreakPointAtom)
                const breakPointId = get(currentBreakPointIdAtom)
                const { selectedNodes = [] } = get(pageInternalStateAtomFamily(stackId))
                const runtimeStateUniqueId = selectedNodes[0]?.scope
                    ? `${selectedNodes[0].scope}@${selectedNodes[0].id}`
                    : selectedNodes[0]?.id
                const containerState = get(containerRuntimeStateAtomFamily({ stackId, containerId: runtimeStateUniqueId || '' }))

                if (isSyncComponent) {
                    const removeUndoRedoInfo: {
                        removedBlocks: BlockAbstract[]
                        prev?: ContainerBlockAbstract[]
                        next?: ContainerBlockAbstract[]
                    } = {
                        removedBlocks: []
                    }
                    set(syncComponentsAtom, draft => {
                        const draftBlock = findBlockById<ContainerBlockAbstract>(selectedBlock.id, draft)
                        if (!draftBlock) {
                            return
                        }

                        const deleteId = draftBlock.config.viewList[payload.index].id

                        Object.entries(value).forEach(([k, v]) => {
                            Reflect.set(draftBlock, k, v)
                        })

                        // 如果当前删除面板是当前选中的面板则重新选中第一个面板
                        if (deleteId === containerState?.currentView) {
                            const firstView = draftBlock.config.viewList.find(item => item.id !== deleteId)
                            if (firstView) {
                                if (breakPointId === mainBreakPoint && draftBlock.config.breakPoint.id === breakPointId) {
                                    if (draftBlock.config.breakPoint.panel?.defaultPanel === deleteId) {
                                        draftBlock.config.breakPoint.panel.defaultPanel = firstView.id
                                    }
                                } else {
                                    const otherBreakPoint = draftBlock.config.breakPoints.find(item => item.id === breakPointId)
                                    if (otherBreakPoint && otherBreakPoint.panel?.defaultPanel === deleteId) {
                                        otherBreakPoint.panel.defaultPanel = firstView.id
                                    }
                                }
                            }
                        }

                        const childrenIndex = draftBlock.children.findIndex(item => item.id === deleteId)
                        if (childrenIndex !== -1) {
                            const removed = draftBlock.children.splice(childrenIndex, 1)
                            removeUndoRedoInfo.removedBlocks = removed.reduce<BlockAbstract[]>(
                                (total, curr) => [...total, ...(isDraft(curr) ? current(curr) : curr).children],
                                []
                            )
                        }

                        removeUndoRedoInfo.prev = original(draft)
                        removeUndoRedoInfo.next = current(draft)

                        set(initContainerRuntimeStateAtom, {
                            stackId,
                            blocks: get(pageBlocksAtom(pageId)),
                            syncComponents: removeUndoRedoInfo.next || []
                        })
                    })

                    if (!removeUndoRedoInfo.next) {
                        return
                    }

                    pageUndoRedoController.add({
                        action: NODE_UPDATE_ACTION,
                        payload: {
                            removed: {
                                removedSyncComponents: removeUndoRedoInfo.removedBlocks,
                                prevSyncComponents: removeUndoRedoInfo.prev,
                                nextSyncComponents: removeUndoRedoInfo.next
                            }
                        }
                    })

                    setContainerConfigureLoading(true)
                    await srv.deleteSyncLeafBlocks({
                        pageId,
                        removedBlocks: removeUndoRedoInfo.removedBlocks,
                        blocks: removeUndoRedoInfo.next
                    })
                    setContainerConfigureLoading(false)
                } else {
                    const whenUpdateConfigUndoRedoInfo: {
                        prev?: BlockAbstract
                        next?: BlockAbstract
                        blocks: {
                            prev?: BlockAbstract[]
                            next?: BlockAbstract[]
                        }
                    } = {
                        blocks: {}
                    }

                    const removeUndoRedoInfo: {
                        removedBlocks: BlockAbstract[]
                        prevBlocks?: BlockAbstract[]
                        nextBlocks?: BlockAbstract[]
                    } = {
                        removedBlocks: []
                    }

                    set(blocksAtom, draft => {
                        const draftBlocks = draft[pageId]
                        if (!draftBlocks) {
                            return
                        }

                        const draftBlock = findBlockById<ContainerBlockAbstract>(selectedBlock.id, draftBlocks)
                        if (!draftBlock) {
                            return
                        }
                        const deleteId = draftBlock.config.viewList[payload.index].id
                        const childrenIndex = draftBlock.children.findIndex(item => item.id === deleteId)
                        if (childrenIndex !== -1) {
                            const removed = draftBlock.children.splice(childrenIndex, 1)
                            removeUndoRedoInfo.removedBlocks = removed.reduce<BlockAbstract[]>(
                                (total, curr) => [...total, ...(isDraft(curr) ? current(curr) : curr).children],
                                []
                            )
                        }

                        removeUndoRedoInfo.prevBlocks = original(draftBlocks)
                        removeUndoRedoInfo.nextBlocks = current(draftBlocks)

                        draftBlock.config.viewList.splice(payload.index, 1)
                        whenUpdateConfigUndoRedoInfo.prev = current(draftBlock)
                        whenUpdateConfigUndoRedoInfo.blocks.prev = current(draftBlocks)

                        // Object.entries(value).forEach(([k, v]) => {
                        //     Reflect.set(draftBlock, k, v)
                        // })

                        // 如果当前删除面板是当前选中的面板则重新选中第一个面板
                        if (deleteId === containerState?.currentView) {
                            const firstView = draftBlock.config.viewList.find(item => item.id !== deleteId)
                            if (firstView) {
                                if (breakPointId === mainBreakPoint && breakPointId === draftBlock.config.breakPoint.id) {
                                    if (draftBlock.config.breakPoint.panel?.defaultPanel === deleteId) {
                                        draftBlock.config.breakPoint.panel.defaultPanel = firstView.id
                                    }
                                } else {
                                    const otherBreakPoint = draftBlock.config.breakPoints.find(item => item.id === breakPointId)
                                    if (otherBreakPoint && otherBreakPoint.panel?.defaultPanel === deleteId) {
                                        otherBreakPoint.panel.defaultPanel = firstView.id
                                    }
                                }

                                set(setContainerRuntimeStateAtom, {
                                    stackId,
                                    containerId: runtimeStateUniqueId,
                                    currentView: firstView.id
                                })
                            }
                        }

                        whenUpdateConfigUndoRedoInfo.next = current(draftBlock)
                        whenUpdateConfigUndoRedoInfo.blocks.next = current(draftBlocks)
                    })

                    if (
                        !removeUndoRedoInfo.prevBlocks ||
                        !removeUndoRedoInfo.nextBlocks ||
                        !whenUpdateConfigUndoRedoInfo.prev ||
                        !whenUpdateConfigUndoRedoInfo.next ||
                        !whenUpdateConfigUndoRedoInfo.blocks.prev ||
                        !whenUpdateConfigUndoRedoInfo.blocks.next
                    ) {
                        return
                    }

                    pageUndoRedoController.add([
                        {
                            action: NODE_UPDATE_ACTION,
                            payload: {
                                removed: {
                                    removedBlocks: removeUndoRedoInfo.removedBlocks,
                                    prevBlocks: removeUndoRedoInfo.prevBlocks,
                                    nextBlocks: removeUndoRedoInfo.nextBlocks
                                }
                            }
                        },
                        {
                            action: BLOCK_UPDATE_ACTION,
                            payload: {
                                prev: whenUpdateConfigUndoRedoInfo.prev,
                                next: whenUpdateConfigUndoRedoInfo.next,
                                blocks: {
                                    prev: whenUpdateConfigUndoRedoInfo.blocks.prev,
                                    next: whenUpdateConfigUndoRedoInfo.blocks.next
                                }
                            }
                        }
                    ])

                    setContainerConfigureLoading(true)

                    // 先删除block，再更新block面板配置
                    await srv.deleteBlock({
                        removedBlocks: removeUndoRedoInfo.removedBlocks,
                        blocks: removeUndoRedoInfo.nextBlocks,
                        pageId
                    })

                    await srv.updateBlock({
                        pageId,
                        updatedBlocks: [whenUpdateConfigUndoRedoInfo.next],
                        allBlocks: whenUpdateConfigUndoRedoInfo.blocks.next
                    })
                    setContainerConfigureLoading(false)
                }
            },
            [pageId, selectedBlock, stackId]
        )
    )

    const handleCloneContainerView = useAtomCallback(
        useCallback(
            async (get, set, value: BlockAbstract, payload: MakeADTMember<'action', BlockSettingActionADT, 'cloneContainerView'>) => {
                if (!selectedBlock) {
                    return
                }

                const isSyncComponent = !!selectedBlock.isMasterSynchronous || !!selectedBlock.isLeafSynchronous

                if (isSyncComponent) {
                    const whenUpdateConfigUndoRedoInfo: {
                        prev?: BlockAbstract
                        next?: BlockAbstract
                        syncComponents: {
                            prev?: ContainerBlockAbstract[]
                            next?: ContainerBlockAbstract[]
                        }
                    } = { syncComponents: {} }

                    const createSyncComponentsUndoRedoInfo: {
                        createdBlocks: BlockAbstract[]
                        prevBlocks?: ContainerBlockAbstract[]
                        nextBlocks?: ContainerBlockAbstract[]
                    } = { createdBlocks: [] }
                    set(syncComponentsAtom, draft => {
                        const draftBlock = findBlockById<ContainerBlockAbstract>(selectedBlock.id, draft)
                        if (!draftBlock) {
                            return
                        }

                        Object.entries(value).forEach(([k, v]) => {
                            Reflect.set(draftBlock, k, v)
                        })

                        const fromPanelId = draftBlock.config.viewList[payload.fromIndex].id
                        const beCopyPanel = draftBlock.children.find(item => item.id === fromPanelId)
                        if (!beCopyPanel) {
                            return
                        }
                        createSyncComponentsUndoRedoInfo.createdBlocks = copyBlock(beCopyPanel.children)
                        set(blockCreatingListAtom, s => [
                            ...s,
                            ...getFlatAllBlock(createSyncComponentsUndoRedoInfo.createdBlocks).map(item => item.id)
                        ])

                        const newPanelWhenUpdateConfig = { id: payload.newId, children: [] }
                        draftBlock.children.splice(payload.fromIndex + 1, 0, newPanelWhenUpdateConfig)
                        whenUpdateConfigUndoRedoInfo.prev = original(draftBlock)
                        whenUpdateConfigUndoRedoInfo.next = current(draftBlock)
                        whenUpdateConfigUndoRedoInfo.syncComponents.prev = original(draft)
                        whenUpdateConfigUndoRedoInfo.syncComponents.next = current(draft)

                        const newPanel = { id: payload.newId, children: createSyncComponentsUndoRedoInfo.createdBlocks }
                        draftBlock.children.splice(payload.fromIndex + 1, 1, newPanel)

                        createSyncComponentsUndoRedoInfo.prevBlocks = original(draft)
                        createSyncComponentsUndoRedoInfo.nextBlocks = current(draft)

                        set(initContainerRuntimeStateAtom, {
                            stackId,
                            blocks: get(pageBlocksAtom(pageId)),
                            syncComponents: createSyncComponentsUndoRedoInfo.nextBlocks || []
                        })
                    })

                    if (
                        !createSyncComponentsUndoRedoInfo.nextBlocks ||
                        !whenUpdateConfigUndoRedoInfo.prev ||
                        !whenUpdateConfigUndoRedoInfo.next ||
                        !whenUpdateConfigUndoRedoInfo.syncComponents.prev ||
                        !whenUpdateConfigUndoRedoInfo.syncComponents.next
                    ) {
                        return
                    }

                    pageUndoRedoController.add([
                        {
                            action: BLOCK_UPDATE_ACTION,
                            payload: {
                                prev: whenUpdateConfigUndoRedoInfo.prev,
                                next: whenUpdateConfigUndoRedoInfo.next,
                                syncComponents: {
                                    prev: whenUpdateConfigUndoRedoInfo.syncComponents.prev,
                                    next: whenUpdateConfigUndoRedoInfo.syncComponents.next
                                }
                            }
                        },
                        {
                            action: NODE_UPDATE_ACTION,
                            payload: {
                                created: {
                                    createdBlocks: createSyncComponentsUndoRedoInfo.createdBlocks,
                                    prevSyncComponents: createSyncComponentsUndoRedoInfo.prevBlocks,
                                    nextSyncComponents: createSyncComponentsUndoRedoInfo.nextBlocks
                                }
                            }
                        }
                    ])

                    setContainerConfigureLoading(true)
                    await srv.updateSyncComponents(whenUpdateConfigUndoRedoInfo.syncComponents.next)

                    await srv.createSyncBlock({
                        createBlocks: createSyncComponentsUndoRedoInfo.createdBlocks,
                        blocks: createSyncComponentsUndoRedoInfo.nextBlocks
                    })
                    setContainerConfigureLoading(false)
                    set(blockCreatingListAtom, s =>
                        s.filter(id => !getFlatAllBlock(createSyncComponentsUndoRedoInfo.createdBlocks).some(item => item.id === id))
                    )
                } else {
                    const whenUpdateConfigUndoRedoInfo: {
                        prev?: BlockAbstract
                        next?: BlockAbstract
                        blocks: {
                            prev?: BlockAbstract[]
                            next?: BlockAbstract[]
                        }
                    } = { blocks: {} }

                    const createBlockUndoRedoInfo: {
                        createdBlocks: BlockAbstract[]
                        prevBlocks?: BlockAbstract[]
                        nextBlocks?: BlockAbstract[]
                    } = {
                        createdBlocks: []
                    }

                    set(blocksAtom, draft => {
                        const draftBlocks = draft[pageId]
                        if (!draftBlocks) {
                            return
                        }

                        const draftBlock = findBlockById<ContainerBlockAbstract>(selectedBlock.id, draftBlocks)
                        if (!draftBlock) {
                            return
                        }

                        Object.entries(value).forEach(([k, v]) => {
                            Reflect.set(draftBlock, k, v)
                        })

                        const fromPanelId = draftBlock.config.viewList[payload.fromIndex].id
                        const beCopyPanel = draftBlock.children.find(item => item.id === fromPanelId)
                        if (!beCopyPanel) {
                            return
                        }
                        createBlockUndoRedoInfo.createdBlocks = copyBlock(beCopyPanel.children)

                        set(blockCreatingListAtom, s => [
                            ...s,
                            ...getFlatAllBlock(createBlockUndoRedoInfo.createdBlocks).map(item => item.id)
                        ])

                        const newPanelWhenUpdateConfig = { id: payload.newId, children: [] }
                        draftBlock.children.splice(payload.fromIndex + 1, 0, newPanelWhenUpdateConfig)
                        whenUpdateConfigUndoRedoInfo.prev = original(draftBlock)
                        whenUpdateConfigUndoRedoInfo.next = current(draftBlock)
                        whenUpdateConfigUndoRedoInfo.blocks.prev = original(draftBlocks)
                        whenUpdateConfigUndoRedoInfo.blocks.next = current(draftBlocks)

                        const newPanel = { id: payload.newId, children: createBlockUndoRedoInfo.createdBlocks }
                        draftBlock.children.splice(payload.fromIndex + 1, 1, newPanel)

                        createBlockUndoRedoInfo.prevBlocks = original(draftBlocks)
                        createBlockUndoRedoInfo.nextBlocks = current(draftBlocks)
                    })

                    if (
                        !createBlockUndoRedoInfo.nextBlocks ||
                        !whenUpdateConfigUndoRedoInfo.prev ||
                        !whenUpdateConfigUndoRedoInfo.next ||
                        !whenUpdateConfigUndoRedoInfo.blocks.prev ||
                        !whenUpdateConfigUndoRedoInfo.blocks.next
                    ) {
                        return
                    }

                    pageUndoRedoController.add([
                        {
                            action: BLOCK_UPDATE_ACTION,
                            payload: {
                                prev: whenUpdateConfigUndoRedoInfo.prev,
                                next: whenUpdateConfigUndoRedoInfo.next,
                                blocks: {
                                    prev: whenUpdateConfigUndoRedoInfo.blocks.prev,
                                    next: whenUpdateConfigUndoRedoInfo.blocks.next
                                }
                            }
                        },
                        {
                            action: NODE_UPDATE_ACTION,
                            payload: {
                                created: {
                                    createdBlocks: createBlockUndoRedoInfo.createdBlocks,
                                    prevBlocks: createBlockUndoRedoInfo.prevBlocks,
                                    nextBlocks: createBlockUndoRedoInfo.nextBlocks
                                }
                            }
                        }
                    ])

                    setContainerConfigureLoading(true)

                    // 先更新面板配置，再创建面板下的block
                    await srv.updateBlock({
                        pageId,
                        updatedBlocks: [whenUpdateConfigUndoRedoInfo.next],
                        allBlocks: whenUpdateConfigUndoRedoInfo.blocks.next
                    })

                    await set(createBlockAtom, {
                        pageId,
                        stackId,
                        createdBlocks: createBlockUndoRedoInfo.createdBlocks,
                        blocks: createBlockUndoRedoInfo.nextBlocks,
                        autoSelect: false
                    })
                    setContainerConfigureLoading(false)
                    set(blockCreatingListAtom, s =>
                        s.filter(id => !getFlatAllBlock(createBlockUndoRedoInfo.createdBlocks).some(item => item.id === id))
                    )
                }
            },
            [pageId, selectedBlock, stackId]
        )
    )

    const onChange = useAtomCallback<void, [BlockAbstract]>(
        useCallback(
            (get, set, value) => {
                if (!selectedBlock) {
                    return
                }

                if (value.type === BlockType.container && selectedBlock.type === BlockType.container && blockSettingActionRef.current) {
                    makeMatchI('action')(blockSettingActionRef.current)({
                        createContainerView: payload => handleCreateContainerView(value, payload),
                        deleteContainerView: payload => handleDeleteContainerView(value, payload),
                        cloneContainerView: payload => handleCloneContainerView(value, payload)
                    })

                    blockSettingActionRef.current = null

                    return
                }

                if (value.type === BlockType.view && selectedBlock.type === BlockType.view) {
                    // 切换视图数据源时初始化配置
                    if (value.config.pointer !== selectedBlock.config.pointer) {
                        const newDatasource = dataSourceList.find(item => item.id === value.config.pointer)
                        if (!newDatasource) {
                            return
                        }

                        const { viewOptions, schema } = newDatasource

                        if (value.config.viewType === 'gallery') {
                            Object.assign(value.config, generateGalleryConfig(newDatasource))
                        }

                        if (value.config.viewType === 'calendar') {
                            Object.assign(value.config, generateCalendarConfig(newDatasource))
                        }

                        const viewFieldSettings = getViewColumns({
                            tableProps: viewOptions.tableProps,
                            disabledFieldName: true,
                            schema
                        })
                        Object.assign(value.config, {
                            viewFieldSettings,
                            quickFilter: { mode: 'normal', rules: [] },
                            groupByFieldId: undefined,
                            groupConfig: undefined,
                            linkFilterController: {
                                expression: {
                                    where: 'AND',
                                    conditions: []
                                }
                            }
                        })
                    }

                    // 切换视图类型时，清空其他类型视图配置
                    if (value.config.viewType !== selectedBlock.config.viewType) {
                        switch (value.config.viewType) {
                            case 'advancedTable':
                            case 'list':
                            case 'table': {
                                Object.assign(value.config, {
                                    ...cleanGalleryConfig,
                                    ...cleanCalendarConfig,
                                    ...cleanKanbanConfig
                                } satisfies Partial<ViewOptions>)
                                break
                            }

                            case 'calendar': {
                                Object.assign(value.config, {
                                    ...cleanGalleryConfig,
                                    ...cleanKanbanConfig
                                } satisfies Partial<ViewOptions>)
                                break
                            }

                            case 'gallery': {
                                Object.assign(value.config, {
                                    ...cleanCalendarConfig,
                                    ...cleanKanbanConfig
                                } satisfies Partial<ViewOptions>)
                                break
                            }

                            case 'kanban': {
                                Object.assign(value.config, {
                                    ...cleanCalendarConfig,
                                    ...cleanGalleryConfig
                                } satisfies Partial<ViewOptions>)
                                break
                            }

                            default: {
                                break
                            }
                        }
                    }
                }

                if (value.type === BlockType.chart && selectedBlock.type === BlockType.chart) {
                    if (chartTypeLevel[value.config.chartType] !== chartTypeLevel[selectedBlock.config.chartType]) {
                        switch (value.config.chartType) {
                            case ChartType.bar:
                            case ChartType.line:
                            case ChartType.composite: {
                                Object.assign(value.config, cleanBarConfig)
                                break
                            }

                            case ChartType.striation: {
                                Object.assign(value.config, cleanStriationConfig)
                                break
                            }

                            case ChartType.pie:
                            case ChartType.funnel: {
                                Object.assign(value.config, cleanPieConfig)
                                break
                            }

                            case ChartType.radar: {
                                Object.assign(value.config, cleanRadarConfig)
                                break
                            }

                            case ChartType.indicator: {
                                Object.assign(value.config, cleanIndicatorConfig)
                                break
                            }

                            default: {
                                break
                            }
                        }
                        // Object.assign(value, getInitChartBlockConfigurator(value, newDatasource)
                        // )
                    }

                    const { config } = value
                    const { config: selectConfig } = selectedBlock
                    if (config.pointer !== selectConfig.pointer) {
                        const newDatasource = dataSourceList.find(item => item.id === config.pointer)
                        if (newDatasource) {
                            Object.assign(value, getInitChartBlockConfigurator(value, newDatasource))
                        }
                    }
                }

                onUpdateBlock({ prevBlock: selectedBlock, nextBlock: value })
                debounceAdd({ prevBlock: selectedBlock, nextBlock: value })
            },
            [
                dataSourceList,
                debounceAdd,
                handleCloneContainerView,
                handleCreateContainerView,
                handleDeleteContainerView,
                onUpdateBlock,
                selectedBlock
            ]
        )
    )

    return selectedBlock && selectedNode ? (
        <BlockSettings
            key={selectedBlock.id}
            ref={ref}
            appId={appId}
            containerConfigureLoading={containerConfigureLoading}
            value={selectedBlock}
            node={selectedNode}
            onChange={onChange}
            dataSource={dataSource}
            dataSourceList={dataSourceList}
            allDataSources={dataSourceList}
            allPages={allPages}
            pointer={pointer}
        />
    ) : null
})

export default BlockSettingsController
